import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const RecordUploads = ({ recentUploads, filterRole }) => {
  const filteredUploads = useMemo(() => {
    if (filterRole === 'all') return recentUploads;
    return recentUploads?.filter(record => record?.type === filterRole);
  }, [recentUploads, filterRole]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'player': return 'User';
      case 'coach': return 'Clipboard';
      case 'goalkeeper': return 'Shield';
      default: return 'FileText';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'player': return 'bg-primary/10 text-primary';
      case 'coach': return 'bg-secondary/10 text-secondary';
      case 'goalkeeper': return 'bg-accent/10 text-accent';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'player': return 'Player';
      case 'coach': return 'Coach';
      case 'goalkeeper': return 'Goalkeeper';
      default: return 'Unknown';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date?.toLocaleDateString();
  };

  const getMediaCount = (record) => {
    let count = 0;
    if (record?.playerPhoto || record?.coachPhoto || record?.goalkeeperPhoto) count++;
    if (record?.matchFootage || record?.matchHighlights) count++;
    if (record?.medicalReports || record?.certificates) count++;
    return count;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Recent Record Uploads</h2>
          <p className="text-sm text-muted-foreground">
            Latest additions to the performance database
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-lg">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-sm font-medium">Live Updates</span>
        </div>
      </div>

      {filteredUploads?.length > 0 ? (
        <div className="space-y-4">
          {filteredUploads?.map((record) => (
            <div
              key={record?.id}
              className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg ${getTypeColor(record?.type)} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={getTypeIcon(record?.type)} size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-foreground truncate">{record?.fullName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(record?.type)}`}>
                          {getTypeBadge(record?.type)}
                        </span>
                        {record?.type === 'player' && record?.position && (
                          <span className="text-xs text-muted-foreground">{record?.position}</span>
                        )}
                        {record?.type === 'coach' && record?.coachingLicense && (
                          <span className="text-xs text-muted-foreground">{record?.coachingLicense}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTimeAgo(record?.createdAt)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    {record?.type === 'player' && (
                      <>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Current Team</p>
                          <p className="text-sm font-medium text-foreground truncate">{record?.currentTeam || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Nationality</p>
                          <p className="text-sm font-medium text-foreground truncate">{record?.nationality || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Goals</p>
                          <p className="text-sm font-medium text-foreground">{record?.goalsScored || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Assists</p>
                          <p className="text-sm font-medium text-foreground">{record?.assists || 0}</p>
                        </div>
                      </>
                    )}
                    {record?.type === 'coach' && (
                      <>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Current Club</p>
                          <p className="text-sm font-medium text-foreground truncate">{record?.presentClub || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Nationality</p>
                          <p className="text-sm font-medium text-foreground truncate">{record?.nationality || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Games Managed</p>
                          <p className="text-sm font-medium text-foreground">{record?.totalGamesManaged || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Wins</p>
                          <p className="text-sm font-medium text-foreground">{record?.gamesWon || 0}</p>
                        </div>
                      </>
                    )}
                    {record?.type === 'goalkeeper' && (
                      <>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Current Team</p>
                          <p className="text-sm font-medium text-foreground truncate">{record?.currentTeam || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Nationality</p>
                          <p className="text-sm font-medium text-foreground truncate">{record?.nationality || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Clean Sheets</p>
                          <p className="text-sm font-medium text-foreground">{record?.cleanSheets || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Games Played</p>
                          <p className="text-sm font-medium text-foreground">{record?.gamesPlayed || 0}</p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Paperclip" size={14} />
                      <span>{getMediaCount(record)} media file{getMediaCount(record) !== 1 ? 's' : ''}</span>
                    </div>
                    {(record?.availableForTransfer) && (
                      <div className="flex items-center gap-2 text-xs text-success">
                        <Icon name="TrendingUp" size={14} />
                        <span>Available for Transfer</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="Upload" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Recent Uploads</h3>
          <p className="text-muted-foreground">No records have been uploaded recently for the selected filter.</p>
        </div>
      )}
    </div>
  );
};

export default RecordUploads;